import {
    call, select, put, takeLatest, takeEvery, delay, fork,
} from "redux-saga/effects";
import { IStoreState } from "@montana/store/store";
import { fetchCart } from "@montana/services/cart";
import { getTranslation } from "../translations/translations.sagas";
import { NotificationLevel } from "../notification/notification";
import { pushNotification } from "../notification/notification.actions";

import {
    CartActionTypes,
    IAddCartLineAction,
    updateCart,
    IPlaceOrderAction,
    placeOrderSuccess,
    placeOrderError,
    setDeleteBuffer,
    getCart,
    IUpdateCartAction,
    IUpdateCartLineQuantityAction,
    updateCartCompleted,
    IReinstateCartLineAction,
    IAddVoucherAction,
    IRemoveVoucherAction
} from "./cart.actions";
import { ICartContainerState, IAppCartLine } from "./cart";
import { putOrder, postLine, putLine, postVoucherCode, removeVoucherCode } from '../../services/cart/cart.service';
import { ICart, ICartLineByProduct, IAddLinesGroupedByProduct } from '../../models/generated/cms.interface';
import { AuthenticationAction } from "../authentication/authentication.actions";
import { push } from "connected-react-router";
import { AppActions } from "../app/app.actions";
import { setCartCookie, getCartCookie, deleteCartCookie } from "@montana/helpers/cart.helper";
import { connectServiceFunctions } from "../base/base.sagas";
import { OmitConfig } from "@montana/models/dev";
import { IDynamicSettingsContainerState } from "../dynamic-settings/dynamic-settings";

export const UNDO_REMOVAL_TIMING = 5000;

export function* onGetCart() {
    try {
        const existingCartGuid = getCartCookie();

        const [cFetchCart]: any[] = yield connectServiceFunctions(fetchCart);
        const response: Response = yield call(cFetchCart, existingCartGuid);
        const { json, ok } = response;

        if (!ok) {
            throw new Error();
        }

        const cart: ICart = yield call(json.bind(response));
        yield put(updateCart(cart));
    } catch (e) {
        const fallbackMessage = "Der opstod en fejl, kurven kunne desværre ikke hentes";
        const errorMessage: string = yield getTranslation((t) => t.cart.getBasketError, fallbackMessage);
        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
    }
}

function* onGetNewCart(onlyDelete = false) {
    deleteCartCookie();
    if( onlyDelete ) {
        return;
    }
    yield put(getCart());
}

/**
 * @throws on http error.
 */
function* addLines(lines: IAddLinesGroupedByProduct[]) {
    const { cartGuid }: ICartContainerState = yield select((state: IStoreState) => state.cart);

    const [cPostLine]: [OmitConfig<typeof postLine>] = yield connectServiceFunctions(postLine);
    const response: Response = yield call(cPostLine, cartGuid, lines);

    const { json, ok } = response;

    if (!ok) {
        throw new Error();
    }

    const cart: ICart = yield call(json.bind(response));

    return cart;
}

function* onAddCartLine(action: IAddCartLineAction) {
    const { lines } = action;
    try {
        if (!lines?.length) { return; }

        const mappedLines: IAddLinesGroupedByProduct[] = lines.map<IAddLinesGroupedByProduct>(line => ({
            productGroupId: 0,
            productsList: [line]
        }));

        const cart: ICart = yield addLines(mappedLines);
        yield put(updateCart(cart));
    } catch (e) {
        const fallbackMessage = "Der opstod en fejl, kurven blev desværre ikke opdateret";
        const errorMessage: string = yield getTranslation((t) => t.basket.updateBasketError, fallbackMessage);

        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
    } finally {
        yield put(updateCartCompleted());
    }
}

function* onUpdateCartLineQuantity(action: IUpdateCartLineQuantityAction) {
    const { productGroupId, quantity, withDebounce = false } = action;
    try {
        if (quantity < 0) { return; }
        if (withDebounce) {
            yield delay(200);
        }

        const { lines }: ICartContainerState = yield select((state: IStoreState) => state.cart);

        const lineToUpdate = lines && lines.find(l => l.productGroupId === productGroupId);

        // If the line is missing or not an actual line (meaning in deleted state) discontinue.
        if (!lineToUpdate) {
            return;
        }

        // Quantity 0 means removing - add line to deletebuffer to allow for cancellation
        if (quantity === 0) {
            yield fork(updateDeleteBuffer, lineToUpdate || productGroupId, quantity);
        }

        const { cartGuid }: ICartContainerState = yield select((state: IStoreState) => state.cart);

        const [cPutLine]: any[] = yield connectServiceFunctions(putLine);
        const response: Response = yield call(cPutLine, cartGuid, productGroupId, quantity);

        const { json, ok } = response;
        if (!ok) {
            throw new Error();
        }
        const cart: ICart = yield call(json.bind(response));

        yield put(updateCart(cart));
    } catch (e) {
        const fallbackMessage = 'Failed to update the basket';
        const errorMessage: string = yield getTranslation((t) => t.basket.updateBasketError, fallbackMessage);

        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
    } finally {
        yield put(updateCartCompleted());
    }

}

function* onReinstateCartLine({ productGroupId }: IReinstateCartLineAction) {
    try {
        const { deleteBuffer, deleteByProductBuffer }: ICartContainerState = yield select((state: IStoreState) => state.cart);

        // find the products related to the line, which we want to reinstate.
        const relevantCartLinesByProduct: ICartLineByProduct[] | undefined = deleteByProductBuffer?.filter(l => l.productGroupId === productGroupId);

        const productsToReinstate: IAddLinesGroupedByProduct[] = [];

        relevantCartLinesByProduct?.forEach(cartLineProduct => {
            const productGuidIndex = productsToReinstate.findIndex(pr => pr.productGroupId === cartLineProduct.productGroupId);

            if (productGuidIndex > -1) {
                productsToReinstate[productGuidIndex]?.productsList?.push({
                    code: cartLineProduct.code || '',
                    lineId: cartLineProduct.lineId,
                    quantity: cartLineProduct.quantity,
                });
            } else {
                productsToReinstate.push({
                    productGroupId: cartLineProduct.productGroupId,
                    productsList: [{
                        code: cartLineProduct.code || '',
                        lineId: cartLineProduct.lineId,
                        quantity: cartLineProduct.quantity,
                    }]
                })
            }
        });

        // If any products, add them to cart as new products, and clean up the deletebuffer.
        if (productsToReinstate?.length) {
            const cart: ICart = yield addLines(productsToReinstate);

            yield put(setDeleteBuffer(
                deleteBuffer?.filter(deletedLine => !relevantCartLinesByProduct?.some(reinstateLine => reinstateLine.productGroupId === deletedLine.productGroupId)),
                deleteByProductBuffer?.filter(deletedLine => !relevantCartLinesByProduct?.some(reinstateLine => reinstateLine.productGroupId === deletedLine.productGroupId)),
            ));

            yield put(updateCart(cart));
        }
    } catch (e) {
        console.error('Failed to reinstate cartline.');
    } finally {
        yield put(updateCartCompleted());
    }
}

function* cleanDeleteBuffer() {
    const { deleteBuffer = [], deleteByProductBuffer = [] }: ICartContainerState = yield select((state: IStoreState) => state.cart);
    const now = new Date().getTime();

    let updatedDeleteBuffer: IAppCartLine[] = [];
    let updatedDeleteByProductBuffer: ICartLineByProduct[] = [];
    deleteBuffer.forEach(deletedLine => {
        if (!deletedLine.deletedTimeStamp || (now - deletedLine.deletedTimeStamp < UNDO_REMOVAL_TIMING)) {
            updatedDeleteBuffer = [...updatedDeleteBuffer, deletedLine];
            updatedDeleteByProductBuffer = [...updatedDeleteByProductBuffer, ...deleteByProductBuffer.filter(l => l.productGroupId === deletedLine.productGroupId)]
        }
    });
    yield put(setDeleteBuffer(deleteBuffer?.filter(i => !i.deletedTimeStamp || (now - i.deletedTimeStamp) < UNDO_REMOVAL_TIMING), updatedDeleteByProductBuffer));
    yield put(updateCartCompleted());
}

function* updateDeleteBuffer(item: IAppCartLine | number, quantity?: number) {
    if (!item) { return; }
    const { deleteBuffer = [], deleteByProductBuffer: deleteByProductBuffer = [], cartLinesByProduct }: ICartContainerState = yield select((state: IStoreState) => state.cart);

    if (typeof item === 'number') {
        yield put(
            setDeleteBuffer(
                deleteBuffer?.filter(l => l.productGroupId !== item),
                deleteByProductBuffer?.filter(l => l.productGroupId !== item)
            )
        );
    } else {
        if (quantity === 0) {
            const deletedItem = { ...item, pendingDeletion: !quantity, deletedTimeStamp: new Date().getTime() };
            yield put(
                setDeleteBuffer(
                    [...deleteBuffer?.filter(i => i.productGroupId !== item.productGroupId), deletedItem],
                    [...deleteByProductBuffer?.filter(i => i.productGroupId !== item.productGroupId), ...(cartLinesByProduct?.filter(i => i.productGroupId === item.productGroupId) || [])]
                )
            );
            yield delay(UNDO_REMOVAL_TIMING);
            yield fork(cleanDeleteBuffer);
        }
    }
}

function* onSetDeleteBuffer() {
    const cart: ICartContainerState = yield select((state: IStoreState) => state.cart);
    const { lines } = cart;
    yield put(updateCart({ ...cart, lines: [...lines] }));
}

function* onPlaceOrder(action: IPlaceOrderAction) {
    try {

        const { deliveryAddress, productReference } = action;
        const { cartGuid }: ICartContainerState = yield select((state: IStoreState) => state.cart);

        const [cPutOrder]: [OmitConfig<typeof putOrder>] = yield connectServiceFunctions(putOrder);
        const response: Response = yield call(cPutOrder, cartGuid, deliveryAddress?.id, productReference);
        const {
            // json,
            ok
        } = response;

        if (!ok) {
            throw new Error();
        }

        yield put(placeOrderSuccess());
        yield put(getCart())

        const { links }: IDynamicSettingsContainerState = yield select((s: IStoreState) => s.dynamicSettings);
        yield put(push(links?.receipt || '/receipt'));

    } catch (e) {
        const fallbackMessage = "Der opstod en fejl, ordren kunne ikke færdiggøres";
        const errorMessage: string = yield getTranslation((t) => t.basket.updateBasketError, fallbackMessage);
        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
        yield put(placeOrderError());
    }
}

function onCartUpdated({ cart }: IUpdateCartAction) {
    cart?.cartGuid && setCartCookie(cart.cartGuid);
}

function* onAddVoucher({ voucherCode }: IAddVoucherAction) {
    try {
        const { cartGuid }: ICartContainerState = yield select((store: IStoreState) => store.cart as unknown as ICartContainerState);

        const [cPostVoucherCode]: any[] = yield connectServiceFunctions(postVoucherCode);
        const response = yield call(cPostVoucherCode, cartGuid, voucherCode);
        const { json, ok } = response;

        if (ok) {
            const cart = yield call(json.bind(response));
            yield put({ type: CartActionTypes.ADD_VOUCHER_SUCCESS });
            yield put(updateCart(cart));
        } else {
            throw new Error();
        }

    } catch(error) {
        const errorMessage = yield getTranslation((t) => t.cartPage.cartPageAddVoucherError, "Couldn't add voucher");

        yield put({ type: CartActionTypes.ADD_VOUCHER_ERROR });
        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
    }
}

function* onRemoveVoucher({ voucherCode }: IRemoveVoucherAction) {
    try {
        const { cartGuid }: ICartContainerState = yield select((store: IStoreState) => store.cart as unknown as ICartContainerState);

        const [cRemoveVoucherCode]: any[] = yield connectServiceFunctions(removeVoucherCode);
        const response = yield call(cRemoveVoucherCode, cartGuid, voucherCode);
        const { json, ok } = response;

        if (ok) {
            const cart = yield call(json.bind(response));
            yield put(updateCart(cart));
        } else {
            throw new Error();
        }

    } catch(error) {
        const errorMessage = yield getTranslation((t) => t.cartPage.cartPageRemoveVoucherError, "Couldn't remove voucher");
        yield put(pushNotification(errorMessage, NotificationLevel.ERROR));
    }
}

export const cartSagas = (isB2b?: boolean) => [
    takeLatest(CartActionTypes.GET, onGetCart),
    takeLatest(CartActionTypes.GET_NEW_CART, onGetNewCart),
    takeLatest(CartActionTypes.PLACE_ORDER, onPlaceOrder),
    takeLatest(CartActionTypes.ADD_CART_LINE, onAddCartLine),
    takeLatest(CartActionTypes.REINSTATE_CART_LINE, onReinstateCartLine),
    takeLatest(CartActionTypes.UPDATE_CART_LINE_QUANTITY, onUpdateCartLineQuantity),
    takeLatest(CartActionTypes.UPDATE_CART, onCartUpdated),
    takeEvery(CartActionTypes.SET_DELETE_BUFFER, onSetDeleteBuffer),
    takeLatest(CartActionTypes.ADD_VOUCHER, onAddVoucher),
    takeLatest(CartActionTypes.REMOVE_VOUCHER, onRemoveVoucher),
    ...(!!isB2b ? [takeLatest(AuthenticationAction.SET_USER, onGetCart)] : [takeLatest(AppActions.INIT, onGetCart)]),
];
